/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';
import objectHash from 'object-hash';

import CommunicatorNumber from '../../communicator/number/communicator-number';
import CommunicatorUtility from '../../communicator/utility/communicator-utility';

/**
 * Komunikátor časovačů řídících úloh pro Ki-Wi Server.
 */
class TimerCommunicator {
  /**
   * Vygeneruje nový identifikátor.
   * @param {DsId} [taskId] Identifikátor úlohy, do které časovač patří.
   * @param {CommandEnum} commandEnum Příkaz k provedení časovačem.
   * @param {number} day Den v týdnu k provedení časovače.
   * @returns {null|string} Identifikátor časovače.
   */
  generateId(taskId, commandEnum, day) {
    return objectHash({ commandEnum, day, taskId });
  }

  /**
   * Zjistí, jestli je pro den nastaven příznak použití.
   * @param {number} [binaryWeek] Zakódované binární příznaky, které dny se mají použít.
   * @param {number} day Den, který se ověřuje.
   * @returns {boolean} Příznak, jestli se má den použít.
   */
  includesDay(binaryWeek, day) {
    // Počet bitových příznaků zakódovaných v čísle.
    const flagCount = 7;
    // Řád čísla, ve kterém je příznak uložen.s
    // Posun o jednu pozici (0 pro pondělí místo neděle).
    const flagPosition = (flagCount + day - 1) % flagCount;
    // Číslo, které má nastaven pouze hledaný řád.
    const flagValue = Math.pow(2, flagPosition);
    // Kontrola, jestli je ověřovaný bit nastaven.
    // Záměrné použití bitwise operátoru.
    // eslint-disable-next-line no-bitwise
    return Boolean(binaryWeek & flagValue);
  }

  /**
   * Zpracuje seznam časovačů a zanořených modelů.
   * @param {Array.<string>} jsonArray Část odpovědi na obsah zařízení se seznamem časovačů.
   * @param {DsId} [taskId] Identifikátor úlohy, do které časovač patří.
   * @param {CommandEnum} commandEnum Příkaz k provedení časovačem.
   * @param {string} [binaryWeekString] Zakódované binární příznaky, které dny se mají použít.
   * @returns {DsMap} Mapa datových atributů ke vložení do datového skladu.
   */
  parseMany(jsonArray, taskId, commandEnum, binaryWeekString) {
    const emptyMap = { timer: {} };
    const binaryWeekNumber = CommunicatorNumber.fromString(binaryWeekString, {
      nil: null
    });
    return CommunicatorUtility.parseMany(
      jsonArray,
      (jsonString, index) =>
        this.parseOne(jsonString, taskId, commandEnum, binaryWeekNumber, index),
      emptyMap
    );
  }

  /**
   * Zpracuje jeden časovač bez zanořených modelů.
   * @param {string} json Část odpovědi na obsah zařízení s jedním časovačem.
   * @param {DsId} [id] Identifikátor časovače, která se má zpracovat.
   * @param {DsId} [taskId] Identifikátor úlohy, do které časovač patří.
   * @param {CommandEnum} commandEnum Příkaz k provedení časovačem.
   * @param {number} day Den v týdnu k provedení časovače.
   * @returns {DsAttrs} Obsah časovače ke vložení do datového skladu.
   */
  parseModel(json, id, taskId, commandEnum, day) {
    const clock = CommunicatorUtility.parseClock(json);
    return {
      commandEnum,
      day,
      hour: clock.hour,
      id,
      minute: clock.minute,
      second: clock.second,
      taskId
    };
  }

  /**
   * Zpracuje jeden časovač a zanořené modely.
   * @param {string} json Část odpovědi na obsah zařízení s jedním časovačem.
   * @param {DsId} [taskId] Identifikátor úlohy, do které časovač patří.
   * @param {CommandEnum} commandEnum Příkaz k provedení časovačem.
   * @param {number} [binaryWeek] Zakódované binární příznaky, které dny se mají použít.
   * @param {number} index Pořadí časovače v seznamu časovačů.
   * @returns {DsMap} Mapa datových atributů ke vložení do datového skladu.
   */
  parseOne(json, taskId, commandEnum, binaryWeek, index) {
    let result;
    const day = CommunicatorUtility.parseDay(index);
    const id = this.generateId(taskId, commandEnum, day);
    if (isNil(id) || !this.includesDay(binaryWeek, day)) {
      result = { timer: {} };
    } else {
      const model = this.parseModel(json, id, taskId, commandEnum, day);
      result = { timer: { [id]: model } };
    }
    return result;
  }
}

export default TimerCommunicator;
